export const NODE_WIDTH = 220;
export const NODE_HEIGHT = 96;
export const H_GAP = 28;
export const V_GAP = 92;

/**
 * Collect ancestor partner_uids (including the node itself) so a deep node can be expanded into view.
 * @param {object[]} flatNodes
 * @param {number} partnerUid
 * @param {number|null} [branchRootUid]
 * @returns {number[]}
 */
export function collectAncestorIds(flatNodes, partnerUid, branchRootUid = null) {
    const byId = new Map(flatNodes.map((node) => [node.partner_uid, node]));
    const ids = [];
    let current = byId.get(partnerUid);

    while (current) {
        ids.push(current.partner_uid);
        const parentId = current.parent_partner_uid;
        if (!parentId || parentId === branchRootUid) {
            break;
        }
        current = byId.get(parentId);
    }

    if (branchRootUid) {
        ids.push(branchRootUid);
    }

    return ids;
}

/**
 * @param {object[]} flatNodes
 * @param {object|null} branchRoot
 * @returns {object[]}
 */
export function buildForest(flatNodes, branchRoot = null) {
    if (branchRoot) {
        const root = {
            ...branchRoot,
            depth: 0,
            parent_partner_uid: null,
            is_root: true,
            has_children: flatNodes.some((n) => n.parent_partner_uid === branchRoot.partner_uid)
                || flatNodes.some((n) => n.depth === 1),
            children_count: flatNodes.filter((n) => n.parent_partner_uid === branchRoot.partner_uid
                || (n.depth === 1 && !n.parent_partner_uid)).length,
        };
        return [attachChildren(root, flatNodes, branchRoot.partner_uid)];
    }

    const byId = new Map();
    for (const node of flatNodes) {
        byId.set(node.partner_uid, { ...node, children: [] });
    }

    const roots = [];
    for (const node of flatNodes) {
        const item = byId.get(node.partner_uid);
        const parentId = node.parent_partner_uid;
        if (node.is_root || (!parentId && node.depth === 0)) {
            roots.push(item);
            continue;
        }
        if (parentId && byId.has(parentId)) {
            byId.get(parentId).children.push(item);
        }
    }

    return roots.sort((a, b) => a.partner_uid - b.partner_uid);
}

/**
 * @param {object} root
 * @param {object[]} flatNodes
 * @param {number} rootUid
 * @returns {object}
 */
function attachChildren(root, flatNodes, rootUid) {
    const byId = new Map([[root.partner_uid, { ...root, children: [] }]]);
    for (const node of flatNodes) {
        byId.set(node.partner_uid, { ...node, children: [] });
    }

    for (const node of flatNodes) {
        const item = byId.get(node.partner_uid);
        const parentId = node.parent_partner_uid ?? (node.depth === 1 ? rootUid : null);
        if (parentId && byId.has(parentId) && parentId !== node.partner_uid) {
            byId.get(parentId).children.push(item);
        }
    }

    const result = byId.get(rootUid);
    result.children.sort((a, b) => a.partner_uid - b.partner_uid);
    sortChildrenRecursive(result);
    return result;
}

/**
 * @param {object} node
 */
function sortChildrenRecursive(node) {
    if (!node.children?.length) {
        return;
    }
    node.children.sort((a, b) => a.partner_uid - b.partner_uid);
    for (const child of node.children) {
        sortChildrenRecursive(child);
    }
}

/**
 * @param {object} node
 * @param {Set<number>} expanded
 * @returns {number}
 */
function measureSubtree(node, expanded) {
    const isExpanded = expanded.has(node.partner_uid);
    if (!node.children?.length || !isExpanded) {
        node._layoutWidth = NODE_WIDTH + H_GAP;
        return node._layoutWidth;
    }

    let width = 0;
    for (const child of node.children) {
        width += measureSubtree(child, expanded);
    }
    node._layoutWidth = Math.max(NODE_WIDTH + H_GAP, width);
    return node._layoutWidth;
}

/**
 * @param {object[]} roots
 * @param {Set<number>} expanded
 * @returns {{ nodes: object[], edges: object[], width: number, height: number }}
 */
export function computeGraphLayout(roots, expanded) {
    const positioned = [];
    const edges = [];
    let offsetX = 40;

    for (const root of roots) {
        measureSubtree(root, expanded);
        const treeWidth = root._layoutWidth;
        layoutNode(root, offsetX, 40, expanded, positioned, edges);
        offsetX += treeWidth + 80;
    }

    let maxX = 0;
    let maxY = 0;
    for (const item of positioned) {
        maxX = Math.max(maxX, item.x + NODE_WIDTH);
        maxY = Math.max(maxY, item.y + NODE_HEIGHT);
    }

    return {
        nodes: positioned,
        edges,
        width: Math.max(maxX + 40, 400),
        height: Math.max(maxY + 40, 300),
    };
}

/**
 * @param {object} node
 * @param {number} x
 * @param {number} y
 * @param {Set<number>} expanded
 * @param {object[]} positioned
 * @param {object[]} edges
 */
function layoutNode(node, x, y, expanded, positioned, edges) {
    const centerX = x + node._layoutWidth / 2;
    const nodeX = centerX - NODE_WIDTH / 2;
    positioned.push({ node, x: nodeX, y });

    const isExpanded = expanded.has(node.partner_uid);
    if (!node.children?.length || !isExpanded) {
        return;
    }

    let childX = x;
    const childY = y + NODE_HEIGHT + V_GAP;
    for (const child of node.children) {
        const childCenterX = childX + child._layoutWidth / 2;
        edges.push({
            x1: centerX,
            y1: y + NODE_HEIGHT,
            x2: childCenterX,
            y2: childY,
        });
        layoutNode(child, childX, childY, expanded, positioned, edges);
        childX += child._layoutWidth;
    }
}

/**
 * @param {object[]} flatNodes
 * @param {object[]} newNodes
 * @returns {object[]}
 */
export function mergeFlatNodes(flatNodes, newNodes) {
    const byId = new Map(flatNodes.map((n) => [n.partner_uid, n]));
    for (const node of newNodes) {
        byId.set(node.partner_uid, node);
    }
    return [...byId.values()];
}

/**
 * Expand root + L1 branches so L0–L2 nodes are visible on first paint.
 * @param {object[]} flatNodes
 * @param {object|null} [branchRoot]
 * @param {number} [maxDepth=2]
 * @returns {Set<number>}
 */
export function defaultExpandedIds(flatNodes, branchRoot = null, maxDepth = 2) {
    const ids = flatNodes
        .filter((n) => n.has_children && (n.depth ?? 0) < maxDepth)
        .map((n) => n.partner_uid);

    if (branchRoot?.partner_uid) {
        ids.push(branchRoot.partner_uid);
    }

    return new Set(ids);
}
